import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, of, switchMap, throwError } from 'rxjs';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { User } from '../user/user.types';
import { environment } from 'environments/environment';

@Injectable()
export class AuthService
{
    private _authenticated: boolean = false;

    // Access token stored in memory only (not localStorage) to mitigate XSS
    private _accessToken: string = '';

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
     get accessToken(): string
     {
         return this._accessToken;
     }

    set accessToken(token: string)
    {
        this._accessToken = token;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<any>
    {
        // Throw error, if the user is already logged in
        if ( this._authenticated )
        {
            return throwError('User is already logged in.');
        }

        return this._httpClient.post(`${environment.httpBasePath}/admin/user/authentication`, credentials, { withCredentials: true }).pipe(
            switchMap((response: any) => {

                // Set user authentication data
                this.setAuthUserData(response);

                // Return a new observable with the response
                return of(response);
            })
        );
    }

    /**
     * Sign in using the refresh token (httpOnly cookie)
     */
    signInUsingToken(): Observable<any>
    {
        return this._httpClient.post(`${environment.httpBasePath}/admin/user/refresh-token`, {}, { withCredentials: true }).pipe(
            catchError(() =>
                of(false)
            ),
            switchMap((response: any) => {
                if (!response) {
                    return of(false);
                }

                // Set user authentication data
                this.setAuthUserData(response);

                // Return true
                return of(true);
            })
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any>
    {
        // Call server to revoke refresh token cookie
        this._httpClient.post(`${environment.httpBasePath}/admin/user/sign-out`, {}, { withCredentials: true })
            .subscribe({ error: () => {} });

        // Clear in-memory token
        this._accessToken = '';

        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean>
    {
        // Check if the user is logged in
        if ( this._authenticated )
        {
            return of(true);
        }

        // The access token lives in memory only: (re)sign in from the httpOnly refresh cookie.
        return this.signInUsingToken();
    }

    /**
     * Check the authentication status
     */
    checkRoles(roles: string[]): Observable<boolean>
    {
        let role: string = '';

        // Get the user's role
        this._userService.user$.subscribe((user) => {
            role = user.role;
        }).unsubscribe();

        if (!!!roles.includes(role)) {
            return of(false);
        }

        return of(true);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------
    private setAuthUserData(response: any): void {
        // Store access token in memory
        this.accessToken = response.token;

        // Set the authenticated flag to true
        this._authenticated = true;

        let user: User = {
            role: 'admin',
            email: 'admin@admin.com',
            avatar: 'assets/images/avatars/default.jpg'
        };

        const decodedToken = AuthUtils.decodeToken(this.accessToken);
        if (decodedToken &&
            decodedToken.email &&
            decodedToken.role) {
            user = {
                role: decodedToken.role,
                email: decodedToken.email,
                avatar: 'assets/images/avatars/default.jpg'
            };
        }

        // Store the user on the user service
        this._userService.user = user;
    }
}
